<template>
  <div class="row">
    <div class="col-lg-8 m-auto">
      <o-card :title="$t('core.login')">
        <form @submit.prevent="login" @keydown="form.onKeydown($event)" v-if="!enable2FA">
          <!-- Email -->
          <div class="form-group row">
            <label class="col-md-3 col-form-label text-md-right">{{ $t('core.email') }}</label>
            <div class="col-md-7">
              <input v-model="form.email" type="email" name="email" class="form-control"
                     :class="{ 'is-invalid': form.errors.has('email') }">
              <has-error :form="form" field="email"></has-error>
            </div>
          </div>

          <!-- Password -->
          <div class="form-group row">
            <label class="col-md-3 col-form-label text-md-right">{{ $t('core.password') }}</label>
            <div class="col-md-7">
              <input v-model="form.password" type="password" name="password" class="form-control"
                     :class="{ 'is-invalid': form.errors.has('password') }">
              <has-error :form="form" field="password"></has-error>
            </div>
          </div>

          <!-- Remember Me -->
          <div class="form-group row">
            <div class="col-md-3"></div>
            <div class="col-md-7">
              <router-link :to="{ name: 'password.request' }" class="float-right small">
                {{ $t('core.forgot_password') }}
              </router-link>

              <b-form-checkbox v-model="remember" class="max-width-65">{{ $t('core.remember_me') }}</b-form-checkbox>
            </div>
          </div>

          <div class="form-group row">
            <div class="col-md-7 offset-md-3 d-flex">
              <!-- Submit Button -->
              <o-button :loading="form.busy" class="mr-3">
                {{ $t('core.login') }}
              </o-button>

              <div class="ml-auto">
                {{ $t('core.login_with_text') }}

                <a v-if="salesForceAuth" href="/oauth/salesforce" class="btn btn-dark ml-1">
                  <fa :icon="['fab', 'salesforce']"/>
                  {{ $t('core.login_with_salesforce') }}
                </a>

                <a v-if="microsoftAuth && primaryDomain" href="/oauth/microsoft" class="btn btn-dark ml-1">
                  <fa :icon="['fab', 'microsoft']"/>
                  {{ $t('core.login_with_azure') }}
                </a>

                <button v-if="aiLoginEnabled" @click="quickAiLogin" type="button" class="btn btn-warning ml-1">
                  AI Quick Login
                </button>
              </div>
            </div>
          </div>
        </form>
        <template v-if="enable2FA">
          <div class="form-group row" v-if="getQRCode">
            <label class="col-md-3 col-form-label text-md-right"></label>
            <div class="col-md-7">
              <p>{{ $t('sandoz.two_factor_info') }}</p>
              <div style="width:40%">
                <img :src="qrCode" v-if="qrCode"/>
                <input v-model="twoFAPassword" type="password" class="form-control mt-1"
                  :class="!validation ? 'is-invalid': ''" :state="validation" :placeholder="$t('sandoz.two_factor_verification_code')">
                <has-error :form="form" field="key"></has-error>
                <b-form-invalid-feedback :state="validation">
                  {{ $t('sandoz.two_factor_confirmation_error') }}
                </b-form-invalid-feedback>
                <button type="button" class="btn btn btn-primary btn-md mt-2" @click="confirmPassword">{{ $t('sandoz.validate_2FA') }}</button>
              </div>
            </div>
          </div>
        </template>
      </o-card>
    </div>
  </div>
</template>

<style>
  .max-width-65 {
    max-width: 65%;
  }
</style>

<script>
  import Form from 'vform'
  import {setLocale} from '~/plugins/i18n'
  import axios from 'axios'

  export default {
    data: () => ({
      form: new Form({
        email: '',
        password: ''
      }),
      remember: false,
      enable2FA: false,
      qrCode: false,
      getQRCode: false,
      twoFAPassword: '',
      userID: null,
      validation: true,
      data: null
    }),

    methods: {
      async login() {
        // Submit the form.
        const {data} = await this.form.post('/api/login')

        this.data = data
        this.userID = data.user.id
        if(data.user && data.user.is_enable_2FA) {
          this.$localStorage.set('jwt-token', data.token)
          this.$localStorage.set('jwt-token-userid', data.user.id)
          this.$localStorage.set('jwt-token-remember', this.remember)
          this.$router.push({name: 'two-factor-auth'})
        } else {
          // if enforce_2FA enable and user not enable 2FA redirect in setting
          if(data.user && data.user.enforce_2a && !data.user.is_enable_2FA) {
            this.enable2FA = true
            this.showQrCode(data.user.id)
          } else {
            this.redirect()
          }
        }
      },
      async quickAiLogin() {
        const { data } = await axios.post('/api/login/ai')

        this.data = data
        this.userID = data.user.id
        this.redirect()
      },
      showQrCode (userID) {
        var data = { user_id: userID}
        return axios.post('/api/settings/generate-code-login', data)
        .then(response => {
          this.qrCode = response.data.path
          this.getQRCode = true
        })
      },
      confirmPassword() {
        if(this.twoFAPassword === '') {
          this.validation = false
        }
      
        axios.post('/api/settings/confirm-password-login', {
            password: this.twoFAPassword,
            user_id: this.userID
        }).then(response => {
            if(response.data.success === true) {
              this.updateEnableFA()
            } else {
              this.validation = false
            }
        })
      },
      updateEnableFA() {
        axios.post('/api/settings/activate-enable-2FA', {
            user_id: this.userID
        }).then(response => {
            if(response.data.success === true) {
              this.redirect()
            } else {
              this.validation = false
            }
        })
      },
      async redirect() {
        // Save the token.
        this.$store.dispatch('auth/saveToken', {
          token: this.data.token,
          remember: this.remember
        })

        // Fetch the user.
        await this.$store.dispatch('auth/fetchUser')

        let lang = this.$store.state['auth']['user']['language']
        if (lang !== null && this.$i18n.locale !== lang) {
          setLocale(lang)
          this.$store.dispatch('lang/setLocale', { 'locale': lang })
        }

        // Redirect to previous page or home in case nothing was specified
        if (this.$route.query && this.$route.query.from) {
          this.$router.push(this.$route.query.from)
        } else {
          this.$router.push({name: 'home'})
        }
      },
    },
   
    
    computed: {
      microsoftAuth: () => window.config.microsoft_auth,
      salesForceAuth: () => window.config.salesforce_auth,
      primaryDomain: () => window.config.primary,
      aiLoginEnabled: () => window.config.aiLoginEnabled
    }
  }
</script>
